import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type PaginationState,
} from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useGetAuth } from "../../../hooks/useGetAuthHook";
import { RetrieveButton } from "./RetrieveButton";
import { DeleteButton } from "./DeleteButton";
import { ResendButton } from "./ResendButton";

interface Invitation {
  id: number;
  email: string;
  role: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

interface PaginatedInvitations {
  current_page: number;
  data: Invitation[];
  last_page: number;
  total: number;
}

const ListInvitations = () => {
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data, isLoading, error } = useGetAuth<PaginatedInvitations>(
    "invitations",
    {
      params: { page: pagination.pageIndex + 1 },
      queryKey: ["get-invitations", pagination.pageIndex + 1],
      placeholderData: keepPreviousData,
    }
  );

  const columns = React.useMemo<ColumnDef<Invitation>[]>(
    () => [
      {
        id: "serialNumber",
        header: "S/N",
        cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex ?? 0;
          const pageSize = table.getState().pagination.pageSize ?? 10;
          const rowIndex = row.index;
          return (
            <div className="text-center font-medium text-gray-700">
              {pageIndex * pageSize + rowIndex + 1}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email Address",
        cell: (info) => (
          <div className="font-medium text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: (info) => (
          <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Sent At",
        cell: (info) => {
          const date = new Date(info.getValue() as string);
          return (
            <div className="text-gray-600 text-sm">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
              <br />
              <span className="text-xs text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "id",
        header: "Actions",
        cell: ({ row }) => {
          const id = row.original.id;
          const email = row.original.email;
          return (
            <div className="flex items-center gap-2">
              {/* <RetrieveButton invitationId={id} /> */}
              <ResendButton invitationId={id} />
              <DeleteButton invitationId={id} />
            </div>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable<Invitation>({
    data: data?.data ?? [],
    columns,
    pageCount: data?.last_page ?? 1,
    state: {
      pagination,
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading invitations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Error loading invitations</p>
          <p className="text-red-600 text-sm mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto">
      {/* Header with Total Count */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-orange-800">
          Pending invitations ({data?.total ?? 0})
        </h2>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-orange-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-orange-500 to-orange-600 border-b border-orange-200">
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-4 text-left text-white font-semibold text-sm"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`hover:bg-orange-50 transition-colors duration-150 ${
                    index % 2 === 0 ? "bg-white" : "bg-orange-50/40"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {table.getRowModel().rows.length === 0 && (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 mb-2">
              <svg
                className="w-12 h-12 mx-auto mb-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <p className="text-gray-600 font-medium">No invitations yet</p>
            <p className="text-gray-500 text-sm mt-1">
              Start by sending out invitations
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg p-4 border border-orange-100">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-orange-600">
            Page {data?.current_page}
          </span>
          {" of "}
          <span className="font-semibold text-orange-600">
            {data?.last_page}
          </span>
          {data?.data && (
            <span className="text-gray-600 ml-4">
              {data.data.length} invitations on this page
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors duration-200"
          >
            <ChevronLeftIcon size={18} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors duration-200"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListInvitations;
